<!--
  Confirm a delete over the listing the row is in, instead of routing away to
  `…/item/delete` and back.

  It posts the SAME request the delete PAGE does — the soft-delete URL, built
  the same way — so what reaches the API is unchanged. Only where the
  confirmation happens moves; on success this refreshes the table behind it
  rather than redirecting to it.

  Opened through the store: the row's Delete button is mounted imperatively by a
  column formatter and has no parent to $emit to (see
  store/modules/modelDeleteDialog.js). A page mounting this registers itself as
  a host, which is what tells that button a dialog exists here at all; where
  none does, it routes as it always has.

  The record is named from the row the button was drawn on rather than from a
  second read — the listing already holds every column the sentence needs.
-->
<template>
  <v-dialog
    v-model="isOpen"
    max-width="480"
    :persistent="api.isLoading"
    content-class="mdd-dialog"
    @click:outside="close"
  >
    <div v-if="isOpen" class="mdd-modal">
      <div class="mdd-modal__head">
        <span class="mdd-modal__icon" aria-hidden="true">
          <v-icon size="18">mdi-trash-can-outline</v-icon>
        </span>
        <h2 class="mdd-modal__title">Delete {{ modelLabel }}</h2>
        <button class="mdd-modal__close" title="Close" @click="close">
          <v-icon size="18">mdi-close</v-icon>
        </button>
      </div>

      <div class="mdd-modal__body">
        <AError :api="api" />

        <p class="mdd-modal__text">
          Are you sure you want to delete {{ modelLabel.toLowerCase() }}
          <b>{{ recordName }}</b
          >?
        </p>
        <p class="mdd-modal__note">
          It moves to Trashed and can be restored from there.
        </p>
      </div>

      <div class="mdd-modal__foot">
        <button
          class="mdd-modal__btn mdd-modal__btn--ghost"
          type="button"
          @click="close"
        >
          Cancel
        </button>
        <v-btn
          class="mdd-modal__btn mdd-modal__btn--danger"
          depressed
          :loading="api.isLoading"
          @click="submit"
        >
          Delete
        </v-btn>
      </div>
    </div>
  </v-dialog>
</template>

<script>
import Api from "@/objects/api";
import Model from "@/objects/model";
import Service from "@/objects/service";
import { mapState } from "vuex";

export default {
  name: "BreadModelDeleteDialog",
  computed: {
    ...mapState({
      deleteRequest: (state) => state.modelDeleteDialog.data.request,
    }),
    modelLabel() {
      return (this.model.name && this.model.name.singular) || "record";
    },
    /**
     * The row's own display column — `name` on internalUser — falling back to
     * the id, which is what the delete page showed for every model.
     */
    recordName() {
      const row = this.request && this.request.row;
      if (!row) return `ID ${this.modelId}`;
      const textColumn = this.model.column && this.model.column.text;
      const title = textColumn ? row[textColumn] : row.name;
      return title || `ID ${this.modelId}`;
    },
  },
  data: () => ({
    api: new Api(),
    model: new Model(),
    service: new Service(),
    request: null,
    modelId: null,
    isOpen: false,
  }),
  watch: {
    /**
     * The store request is a one-shot: it is cleared as soon as it is taken, so
     * deleting from the same row twice still arrives as a change.
     */
    deleteRequest(request) {
      if (!request) return;
      this.$store.dispatch("clearModelDeleteRequest");
      this.open(request);
    },
  },
  created() {
    this.$store.dispatch("registerModelDeleteDialogHost");
  },
  beforeDestroy() {
    this.$store.dispatch("unregisterModelDeleteDialogHost");
  },
  methods: {
    /**
     * `modelKey` is read from the request rather than the route: a child table
     * lists a different model than the page it is on.
     */
    open(request) {
      this.request = request;
      this.model.getByKey(request.modelKey);
      if (this.model.serviceKey) this.service.set("key", this.model.serviceKey);
      this.modelId = request.modelId;

      // Not callbackReset(): despite the name that is Api's pre-flight hook and
      // sets isLoading TRUE, which through `:persistent` would open the dialog
      // spinning and refusing to close.
      this.api.isError = false;
      this.api.error = null;

      this.api.setMethod("POST");
      this.api.setUrl(this.deleteUrl());
      this.api.setCallbackCompleted(() => {
        this.isOpen = false;
        this.$emit("deleted", { modelKey: this.model.key, modelId: this.modelId });
        this.$store.dispatch("showMessage", {
          message: "Deleted successfully.",
          messageType: "success",
          timeout: 2000,
        });
      });

      this.isOpen = true;
    },
    close() {
      if (this.api.isLoading) return;
      this.isOpen = false;
    },
    submit() {
      this.api.fetch();
    },
    deleteUrl() {
      if (this.model.apiUrls.apiSoftDeleteUrl != "") {
        return `${this.$service[this.service.key]}${
          this.model.apiUrls.apiSoftDeleteUrl
        }/${this.modelId}/delete/soft`;
      }
      return `${
        this.$service[this.service.key]
      }/v1/en/console/tabulator/model/${this.model.key}/${
        this.modelId
      }/delete/soft`;
    },
  },
};
</script>

<style scoped>
/* Same tiers as the other dev-model dialogs: chrome on --app-card, the body a
   step back on --app-surface. Token-only colour, so dark needs no companion
   block. */
.mdd-modal {
  background: var(--app-card);
  font-family: var(--tbl-font);
}

.mdd-modal__head {
  display: flex;
  align-items: center;
  gap: 10px;
  padding: 16px 20px;
  background: var(--app-card);
  border-bottom: 1px solid var(--app-border);
}

/* The action is destructive, so it wears the danger colour where the eye lands
   first rather than only on the button that commits it. */
.mdd-modal__icon {
  display: inline-flex;
  align-items: center;
  justify-content: center;
  width: 32px;
  height: 32px;
  flex-shrink: 0;
  border-radius: 9px;
  background: var(--app-danger-bg);
  border: 1px solid var(--app-danger-border);
  color: var(--app-danger-fg);
}

.mdd-modal__icon .v-icon {
  color: var(--app-danger-fg);
}

.mdd-modal__title {
  flex: 1;
  min-width: 0;
  font-size: 0.98rem;
  font-weight: 700;
  color: var(--app-text);
  margin: 0;
}

.mdd-modal__close {
  display: inline-flex;
  align-items: center;
  justify-content: center;
  width: 30px;
  height: 30px;
  flex-shrink: 0;
  border-radius: 8px;
  background: transparent;
  color: var(--app-text-faint);
  transition: all 0.15s ease;
}

.mdd-modal__close:hover {
  background: var(--app-hover);
  color: var(--app-text);
}

.mdd-modal__body {
  padding: 20px;
  background: var(--app-surface);
}

.mdd-modal__text {
  font-size: 0.88rem;
  font-weight: 600;
  line-height: 1.5;
  color: var(--app-text);
  margin: 0;
}

.mdd-modal__text b {
  font-weight: 800;
}

.mdd-modal__note {
  font-size: 0.76rem;
  font-weight: 600;
  color: var(--app-text-faint);
  margin: 8px 0 0;
}

.mdd-modal__foot {
  display: flex;
  align-items: center;
  justify-content: flex-end;
  gap: 10px;
  padding: 14px 20px;
  border-top: 1px solid var(--app-border);
  background: var(--app-card);
}

.mdd-modal__btn {
  height: 38px;
  padding: 0 20px;
  border-radius: 10px;
  font-family: var(--tbl-font);
  font-size: 0.82rem;
  font-weight: 600;
  letter-spacing: normal;
  text-transform: none;
  transition: all 0.15s ease;
}

.mdd-modal__btn--ghost {
  border: 1px solid var(--app-border-strong);
  background: transparent;
  color: var(--app-text-muted);
}

.mdd-modal__btn--ghost:hover {
  background: var(--app-hover);
  color: var(--app-text);
}

.mdd-modal__btn--danger.v-btn {
  background: var(--app-danger) !important;
  color: #fff !important;
  box-shadow: none;
}

.mdd-modal__btn--danger.v-btn:hover {
  filter: brightness(0.94);
}

@media (max-width: 599px) {
  .mdd-modal__head,
  .mdd-modal__foot {
    padding: 12px;
  }

  .mdd-modal__body {
    padding: 14px 12px;
  }

  .mdd-modal__btn {
    flex: 1;
  }
}
</style>

<style>
/* Unscoped: VDialog moves this content out of the component, and `content-class`
   lands on an element it owns rather than on our root, so a scoped selector
   never reaches either. */
.v-dialog.mdd-dialog {
  border-radius: 14px;
  background: var(--app-card);
  border: 1px solid var(--app-border);
  box-shadow: 0 24px 60px var(--app-shadow-deep, rgba(0, 0, 0, 0.35));
}
</style>
